import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Network, ChevronDown, Plus, Pencil, Trash2, Share2, Star } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { useTopologyHeader, type TopologyLayoutState } from '../../context/TopologyHeaderContext'
import { topologyLayoutsApi } from '../../api/topologyLayouts'
import { TopologyLayoutModal } from './TopologyLayoutModal'
import { ConfirmModal } from '../ConfirmModal'
import type { TopologyLayout } from '../../types'

export function TopologyLayoutPicker() {
  const { t } = useTranslation()
  const { user, canWrite } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { getState, getOnRestore } = useTopologyHeader()
  const [isOpen, setIsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingLayout, setEditingLayout] = useState<TopologyLayout | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<TopologyLayout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isAdmin = user?.role === 'Admin'

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const { data: layouts = [] } = useQuery({
    queryKey: ['topology-layouts'],
    queryFn: () => topologyLayoutsApi.getLayouts(),
  })

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof topologyLayoutsApi.createLayout>[0]) => topologyLayoutsApi.createLayout(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topology-layouts'] })
      toast.success(t('topologyLayouts.created'))
      setModalOpen(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TopologyLayout> }) => topologyLayoutsApi.updateLayout(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topology-layouts'] })
      toast.success(t('topologyLayouts.updated'))
      setModalOpen(false)
      setEditingLayout(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => topologyLayoutsApi.deleteLayout(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topology-layouts'] })
      toast.success(t('topologyLayouts.deleted'))
      setConfirmDelete(null)
    },
  })

  const buildStatePayload = (state: TopologyLayoutState) => {
    const positionsWithMeta: Record<string, unknown> = { ...state.nodePositions }
    if (state.expandedServices && state.expandedServices.length > 0) {
      positionsWithMeta.__expandedServices__ = state.expandedServices
    }
    if (state.viewport) {
      positionsWithMeta.__viewport__ = state.viewport
    }
    return {
      nodePositions: JSON.stringify(positionsWithMeta),
      timeRange: state.timeRange,
    }
  }

  const handleSave = (data: { name: string; description?: string; isShared: boolean; isDefault: boolean }) => {
    const state = getState()
    if (!state) return

    if (editingLayout) {
      updateMutation.mutate({ id: editingLayout.id, data: { ...data, ...buildStatePayload(state) } })
    } else {
      createMutation.mutate({
        name: data.name,
        description: data.description,
        isShared: data.isShared,
        isDefault: data.isDefault,
        ...buildStatePayload(state),
      })
    }
  }

  const handleRestore = (layout: TopologyLayout) => {
    getOnRestore()?.(layout)
    setIsOpen(false)
  }

  const handleEdit = (e: React.MouseEvent, layout: TopologyLayout) => {
    e.stopPropagation()
    setEditingLayout(layout)
    setModalOpen(true)
    setIsOpen(false)
  }

  const handleDelete = (e: React.MouseEvent, layout: TopologyLayout) => {
    e.stopPropagation()
    setConfirmDelete(layout)
    setIsOpen(false)
  }

  const canEditLayout = (layout: TopologyLayout) => isAdmin || layout.ownerId === user?.id

  const getNodeCount = (layout: TopologyLayout): number => {
    try {
      const positions = JSON.parse(layout.nodePositions)
      return Object.keys(positions).filter(k => !k.startsWith('__')).length
    } catch {
      return 0
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 lg:px-4 py-1.5 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-white dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition-colors w-full lg:w-[36rem]"
        aria-label={t('topologyLayouts.title')}
        aria-expanded={isOpen}
      >
        <Network size={14} className="shrink-0" />
        <span className="truncate">{t('topologyLayouts.title')}</span>
        <ChevronDown size={14} className={`ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[36rem] bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl z-50">
          <div className="p-2 border-b dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
              {t('topologyLayouts.title')}
            </span>
            {canWrite && (
              <button
                onClick={() => { setEditingLayout(null); setModalOpen(true); setIsOpen(false) }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                title={t('topologyLayouts.saveCurrentState')}
              >
                <Plus size={14} />
                {t('topologyLayouts.save')}
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {layouts.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('topologyLayouts.noLayouts')}
              </div>
            ) : (
              layouts.map(layout => (
                <button
                  key={layout.id}
                  onClick={() => handleRestore(layout)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {layout.isDefault && <Star size={12} className="text-amber-500 flex-shrink-0" />}
                      {layout.isShared && <Share2 size={12} className="text-blue-500 flex-shrink-0" />}
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {layout.name}
                      </span>
                    </div>
                    {layout.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {layout.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      {layout.timeRange && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          {layout.timeRange}
                        </span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                        {getNodeCount(layout)} {t('topologyLayouts.nodes')}
                      </span>
                    </div>
                  </div>
                  {canWrite && canEditLayout(layout) && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={(e) => handleEdit(e, layout)}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                        title={t('topologyLayouts.edit')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, layout)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                        title={t('topologyLayouts.deleteAction')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <TopologyLayoutModal
        isOpen={modalOpen}
        layout={editingLayout}
        onClose={() => { setModalOpen(false); setEditingLayout(null) }}
        onSave={handleSave}
        isAdmin={isAdmin}
      />

      <ConfirmModal
        isOpen={!!confirmDelete}
        title={t('topologyLayouts.deleteConfirmTitle')}
        message={t('topologyLayouts.deleteConfirm', { name: confirmDelete?.name })}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
